import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { dashboardPath, useApi } from "../api/client";
import type { DashboardSummary, TypeRow } from "../api/types";
import { BarChart, LineChart } from "../components/Charts";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { CostCell } from "../components/CostCell";
import { Bar, Empty, ErrorState, Loading } from "../components/States";
import { SubmitterFilter, useSubmittedBy } from "../components/SubmitterFilter";
import { Breadcrumb, CustomRange, SegmentedControl, Toolbar } from "../components/Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import { EM_DASH, confidenceBand, fmtCost, fmtTokens, humanise } from "../lib/format";
import { PERIOD_SEGMENTS, type PeriodKey } from "./DocumentList";

const trendLabel = (iso: string, bucket: string) =>
  new Date(iso).toLocaleDateString(
    "en-GB",
    bucket === "month" ? { month: "short", year: "2-digit" } : { day: "numeric", month: "short" }
  );

export function Dashboard() {
  const [period, setPeriod] = useState<PeriodKey>("last_30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [submittedBy, setSubmittedBy] = useSubmittedBy();

  // A custom range with only one end filled in would query the whole table on
  // every keystroke, so the request waits until both dates are present.
  const ready = period !== "custom" || (from !== "" && to !== "");
  const path = ready
    ? dashboardPath(period, from || undefined, to || undefined, submittedBy || undefined)
    : null;
  const { data, error, loading, reload } = useApi<DashboardSummary>(path);

  const maxDocs = useMemo(
    () => Math.max(1, ...(data?.by_document_type ?? []).map((r) => r.documents)),
    [data]
  );

  const maxTrendDocs = useMemo(
    () => Math.max(1, ...(data?.trend ?? []).map((t) => t.documents)),
    [data]
  );

  const exportCsv = () => {
    if (!data?.by_document_type.length) return;
    downloadCsv(
      stampedName("dashboard-by-type"),
      toCsv<TypeRow>(data.by_document_type, [
        { header: "Document type", value: (r) => r.document_type },
        { header: "Documents", value: (r) => r.documents },
        { header: "% of total", value: (r) =>
            data.totals.documents ? ((r.documents / data.totals.documents) * 100).toFixed(1) : null },
        { header: "Avg confidence %", value: (r) => r.avg_confidence },
        { header: "Tokens", value: (r) => r.total_tokens },
        { header: "Cost USD", value: (r) => r.cost_usd },
        { header: "Documents without usage", value: (r) => r.documents_without_usage },
      ])
    );
  };

  return (
    <>
      <Breadcrumb trail={[{ label: "Agents", to: "/agents" }, { label: "Dashboard" }]} />

      <div className="page-head">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">Documents processed, by type, with token and cost totals.</p>
        </div>
        <div className="range-group">
          {/* The date inputs appear beside the control that asked for them,
              rather than behind an "Add Filter" button whose panel would hold
              nothing else on this page. */}
          <SubmitterFilter value={submittedBy} onChange={setSubmittedBy} idPrefix="dash" />
          {period === "custom" ? (
            <CustomRange
              from={from}
              to={to}
              onFrom={setFrom}
              onTo={setTo}
              idPrefix="dash"
            />
          ) : null}
          <SegmentedControl
            segments={PERIOD_SEGMENTS}
            value={period}
            onChange={(p) => {
              setPeriod(p);
              if (p !== "custom") {
                setFrom("");
                setTo("");
              }
            }}
            label="Time range"
          />
        </div>
      </div>

      {!ready ? (
        <div className="card"><Empty title="Pick both dates" hint="A custom range needs a start and an end." /></div>
      ) : loading ? (
        <div className="card"><Loading what="dashboard" /></div>
      ) : error ? (
        <div className="card"><ErrorState message={error} onRetry={reload} /></div>
      ) : !data ? null : (
        <>
          <div className="tiles">
            <div className="tile">
              <div className="tile-label">Documents</div>
              <div className="tile-value">{fmtTokens(data.totals.documents)}</div>
            </div>
            <div className="tile">
              <div className="tile-label">Tokens</div>
              <div className="tile-value">{fmtTokens(data.totals.total_tokens)}</div>
            </div>
            <div className="tile">
              <div className="tile-label">Estimated cost</div>
              <div className="tile-value">{fmtCost(data.totals.cost_usd)}</div>
              {data.totals.documents_without_usage > 0 ? (
                <div className="tile-note warn">
                  lower bound — {data.totals.documents_without_usage} document
                  {data.totals.documents_without_usage === 1 ? "" : "s"} unrecorded
                </div>
              ) : null}
            </div>
            <div className="tile">
              <div className="tile-label">Avg confidence</div>
              {/* Rendered at tile-value size rather than as a pill badge, so it
                  sits on the same baseline as the three figures beside it. The
                  band colour still carries the reading, and the number is
                  always present so colour is never the only signal. */}
              <div
                className="tile-value"
                style={
                  data.totals.avg_confidence === null
                    ? undefined
                    : { color: `var(--${confidenceBand(data.totals.avg_confidence)})` }
                }
              >
                {data.totals.avg_confidence === null
                  ? EM_DASH
                  : `${data.totals.avg_confidence.toFixed(1)}%`}
              </div>
              <div className="tile-note">
                {data.totals.avg_confidence === null
                  ? "none recorded"
                  : "self-reported, not accuracy"}
              </div>
            </div>
          </div>

          {data.cost_note ? <div className="notice">{data.cost_note}</div> : null}

          <div className="grid-2">
            <div className="card">
              <div className="card-head bordered">
                <span className="card-title">Documents by type</span>
                <span className="muted">{data.by_document_type.length} types</span>
              </div>
              <div className="card-body">
                <BarChart
                  data={data.by_document_type.map((r) => ({
                    label: humanise(r.document_type).slice(0, 10),
                    sub: humanise(r.document_type),
                    value: r.documents,
                  }))}
                  valueLabel="Documents"
                  height={230}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-head bordered">
                <span className="card-title">Documents processed</span>
                <span className="muted">by {data.bucket}</span>
              </div>
              <div className="card-body">
                {/* Documents and tokens are different measures on different
                    scales, so they get a chart each rather than a second
                    y-axis. */}
                <LineChart
                  labels={data.trend.map((t) => trendLabel(t.bucket, data.bucket))}
                  series={[{ name: "Documents", values: data.trend.map((t) => t.documents) }]}
                  height={230}
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head bordered">
              <span className="card-title">Token consumption</span>
              <span className="muted">by {data.bucket}</span>
            </div>
            <div className="card-body">
              {/* Full-width card, so a wide frame: with the default 760 the
                  aspect ratio made this chart roughly twice the height of the
                  two half-width ones above it. */}
              <LineChart
                labels={data.trend.map((t) => trendLabel(t.bucket, data.bucket))}
                series={[{ name: "Tokens", values: data.trend.map((t) => t.total_tokens) }]}
                height={230}
                viewWidth={1800}
              />
            </div>
          </div>

          <Toolbar
            title="Document-type Net Usage Summary"
            subtitle={`Type-wise volume, confidence and cost · ${data.by_document_type.length} types`}
            onExport={exportCsv}
            exportDisabled={!data.by_document_type.length}
          />

          <div className="card">
            {data.by_document_type.length === 0 ? (
              <Empty title="No documents in this period" />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col" className="si">Sl</th>
                      <th scope="col">Document type</th>
                      <th scope="col" className="num">Documents</th>
                      <th scope="col" style={{ width: 120 }}>Share</th>
                      <th scope="col" className="num">% of total</th>
                      <th scope="col">Avg confidence</th>
                      <th scope="col" className="num">Tokens</th>
                      <th scope="col" className="num">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_document_type.map((r, i) => (
                      <tr key={r.document_type}>
                        <td className="si">{i + 1}</td>
                        <td>
                          <Link to={`/documents?document_type=${encodeURIComponent(r.document_type)}`}>
                            {humanise(r.document_type)}
                          </Link>
                        </td>
                        <td className="num">{fmtTokens(r.documents)}</td>
                        <td><Bar value={r.documents} max={maxDocs} /></td>
                        <td className="num">
                          {data.totals.documents
                            ? `${((r.documents / data.totals.documents) * 100).toFixed(1)}%`
                            : EM_DASH}
                        </td>
                        <td><ConfidenceBadge value={r.avg_confidence} /></td>
                        <td className="num">{fmtTokens(r.total_tokens)}</td>
                        <td className="num">
                          <CostCell
                            usd={r.cost_usd}
                            recorded={r.documents_without_usage < r.documents}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row" colSpan={2}>Total</th>
                      <td className="num"><strong>{fmtTokens(data.totals.documents)}</strong></td>
                      <td colSpan={3} />
                      <td className="num"><strong>{fmtTokens(data.totals.total_tokens)}</strong></td>
                      <td className="num"><strong>{fmtCost(data.totals.cost_usd)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-head bordered"><span className="card-title">By status</span></div>
            {data.by_status.length === 0 ? (
              <Empty title="No statuses in this period" />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col" className="si">Sl</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="num">Documents</th>
                      <th scope="col" className="num">% of total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_status.map((s, i) => (
                      <tr key={s.status}>
                        <td className="si">{i + 1}</td>
                        <td>{humanise(s.status)}</td>
                        <td className="num">{fmtTokens(s.documents)}</td>
                        <td className="num">
                          {data.totals.documents
                            ? `${((s.documents / data.totals.documents) * 100).toFixed(1)}%`
                            : EM_DASH}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {data.trend.length > 1 ? (
            <div className="card">
              <div className="card-head bordered">
                <span className="card-title">Trend</span>
                <span className="muted">by {data.bucket}</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col" className="si">Sl</th>
                      <th scope="col">{data.bucket === "month" ? "Month" : "Day"}</th>
                      <th scope="col" className="num">Documents</th>
                      <th scope="col" style={{ width: 160 }}>Volume</th>
                      <th scope="col" className="num">Tokens</th>
                      <th scope="col" className="num">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.trend.map((t, i) => (
                      <tr key={t.bucket}>
                        <td className="si">{i + 1}</td>
                        <td className="nowrap">
                          {new Date(t.bucket).toLocaleDateString("en-GB",
                            data.bucket === "month"
                              ? { month: "short", year: "numeric" }
                              : { day: "2-digit", month: "short" })}
                        </td>
                        <td className="num">{fmtTokens(t.documents)}</td>
                        <td><Bar value={t.documents} max={maxTrendDocs} /></td>
                        <td className="num">{fmtTokens(t.total_tokens)}</td>
                        <td className="num"><CostCell usd={t.cost_usd} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
